import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Loader2,
  Calendar as CalIcon,
  CheckCircle2,
  Save,
  XCircle,
  LogOut,
  KeyRound,
  Phone,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Skeleton } from "../components/ui/skeleton";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import api from "../lib/api";
import {
  portalApi,
  setPortalToken,
  clearPortalToken,
  getPortalToken,
  buildPortalWsUrl,
  playChime,
} from "../lib/portal";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import { renderError } from "../lib/errorMessage";
import ChatPanel from "../components/ChatPanel";

const TRICITY_CITIES = [
  "Bytów", "Chojnice", "Człuchów", "Gdańsk", "Gdynia", "Hel", "Jastarnia",
  "Kartuzy", "Kościerzyna", "Kwidzyn", "Lębork", "Malbork", "Nowy Dwór Gdański",
  "Pelplin", "Pruszcz Gdański", "Puck", "Reda", "Rumia", "Skarszewy", "Słupsk",
  "Sopot", "Starogard Gdański", "Sztum", "Tczew", "Wejherowo", "Władysławowo",
  "Żukowo",
];

const STATUS_COLOURS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-sky-100 text-sky-800 border-sky-200",
  in_progress: "bg-violet-100 text-violet-800 border-violet-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

function formatYMD(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PORTAL_BOOKING_KEY = "lh2o_portal_booking_v1";

function readCachedPortalBooking() {
  try {
    const raw = sessionStorage.getItem(PORTAL_BOOKING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeCachedPortalBooking(booking, editable) {
  try {
    if (booking) {
      sessionStorage.setItem(
        PORTAL_BOOKING_KEY,
        JSON.stringify({ booking, editable }),
      );
    } else {
      sessionStorage.removeItem(PORTAL_BOOKING_KEY);
    }
  } catch (_) { /* ignore */ }
}

export default function CustomerPortal() {
  const { t, lang } = useI18n();
  useSEO({ titleKey: "seo.portal_title", descriptionKey: "seo.portal_desc" });

  // If a portal token exists AND we have a cached booking from this session,
  // render the dashboard instantly and revalidate in the background. No spinner.
  const initialCache = getPortalToken() ? readCachedPortalBooking() : null;
  const [booking, setBooking] = useState(initialCache?.booking || null);
  const [editable, setEditable] = useState(!!initialCache?.editable);
  const [bootLoading, setBootLoading] = useState(
    !!getPortalToken() && !initialCache,
  );

  // Resume / revalidate the portal session whenever a token is present.
  useEffect(() => {
    const t0 = getPortalToken();
    if (!t0) {
      setBootLoading(false);
      return;
    }
    portalApi
      .get("/portal/booking")
      .then((r) => {
        setBooking(r.data.booking);
        setEditable(r.data.editable);
        writeCachedPortalBooking(r.data.booking, r.data.editable);
      })
      .catch(() => {
        clearPortalToken();
        writeCachedPortalBooking(null);
        setBooking(null);
      })
      .finally(() => setBootLoading(false));
  }, []);

  if (bootLoading) {
    return <PortalBootSkeleton />;
  }

  // Wrap setBooking so any update from child components also writes the cache,
  // keeping the instant-render path warm for the next visit.
  const persistSetBooking = (next) => {
    setBooking(next);
    writeCachedPortalBooking(next, editable);
  };
  const persistSetEditable = (next) => {
    setEditable(next);
    writeCachedPortalBooking(booking, next);
  };

  if (!booking) {
    return (
      <LookupForm
        onAuthenticated={(b, ed) => {
          setBooking(b);
          setEditable(ed);
          writeCachedPortalBooking(b, ed);
        }}
      />
    );
  }

  return (
    <PortalDashboard
      booking={booking}
      editable={editable}
      setBooking={persistSetBooking}
      setEditable={persistSetEditable}
      onLogout={() => {
        clearPortalToken();
        writeCachedPortalBooking(null);
        setBooking(null);
        setEditable(false);
      }}
    />
  );
}

// ----------------------------------------------------------------------------
// Lookup form
// ----------------------------------------------------------------------------
function LookupForm({ onAuthenticated }) {
  const { t } = useI18n();
  const [reference, setReference] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  // `errorInfo` is a descriptor (not a translated string) so the message
  // re-renders in the current language whenever the user toggles PL/EN.
  const [errorInfo, setErrorInfo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErrorInfo(null);
    if (!reference.trim() || !contact.trim()) {
      setErrorInfo({ key: "portal.errors.bad_request" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/lookup", {
        reference: reference.trim(),
        contact: contact.trim(),
      });
      setPortalToken(data.token);
      onAuthenticated(data.booking, data.editable);
    } catch (err) {
      setErrorInfo({ err, context: "lookup" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-water-grad py-12 sm:py-16 px-5" data-testid="portal-lookup-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            {t("portal.title")}
          </h1>
          <p className="mt-3 text-slate-600">{t("portal.subtitle")}</p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-3xl bg-white border border-slate-200 shadow-lg p-7 space-y-5"
        >
          <div>
            <Label htmlFor="ref" className="text-xs">
              {t("portal.lookup_reference")}
            </Label>
            <div className="relative mt-1">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="ref"
                value={reference}
                onChange={(e) => setReference(e.target.value.toUpperCase())}
                placeholder={t("portal.lookup_reference_placeholder")}
                className="pl-9 font-mono tracking-wider"
                data-testid="portal-lookup-reference-input"
                autoFocus
              />
            </div>
          </div>
          <div>
            <Label htmlFor="contact" className="text-xs">
              {t("portal.lookup_contact")}
            </Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={t("portal.lookup_contact_placeholder")}
                className="pl-9"
                data-testid="portal-lookup-contact-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              {t("portal.lookup_help")}
            </p>
          </div>

          {errorInfo && (
            <div
              className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700"
              data-testid="portal-lookup-error"
            >
              {renderError(errorInfo, t)}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center inline-flex items-center gap-2"
            data-testid="portal-lookup-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {t("portal.lookup_btn")}
          </button>
        </form>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Authenticated dashboard
// ----------------------------------------------------------------------------
function PortalDashboard({ booking, editable, setBooking, setEditable, onLogout }) {
  const { t, lang } = useI18n();

  return (
    <div className="min-h-screen bg-water-grad py-8 sm:py-12 px-4 sm:px-6" data-testid="portal-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
              {t("portal.title")}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-500">{t("portal.reference")}:</span>
              <span className="font-mono font-semibold text-slate-800" data-testid="portal-reference">
                {booking.reference}
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">{t("portal.status")}:</span>
              <span
                className={`inline-block px-2 py-0.5 rounded-md border text-xs font-semibold ${STATUS_COLOURS[booking.status]}`}
                data-testid="portal-status-badge"
              >
                {t(`status.${booking.status}`)}
              </span>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-rose-600"
            data-testid="portal-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">{t("portal.logout")}</span>
          </button>
        </div>

        {!editable && (
          <div
            className="mb-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800"
            data-testid="portal-readonly-banner"
          >
            {t("portal.readonly_notice").replace(
              "{status}",
              t(`status.${booking.status}`),
            )}
          </div>
        )}

        <Tabs defaultValue="details" className="w-full">
          <TabsList data-testid="portal-tabs">
            <TabsTrigger value="details" data-testid="portal-tab-details">
              {t("portal.details_tab")}
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="portal-tab-chat">
              {t("portal.chat_tab")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-5">
            <BookingEditor
              booking={booking}
              editable={editable}
              setBooking={setBooking}
              setEditable={setEditable}
            />
          </TabsContent>

          <TabsContent value="chat" className="mt-5">
            <CustomerChat booking={booking} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Booking editor (details + reschedule + cancel)
// ----------------------------------------------------------------------------
function BookingEditor({ booking, editable, setBooking, setEditable }) {
  const { t, lang } = useI18n();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    customer_name: booking.customer_name,
    customer_email: booking.customer_email,
    customer_phone: booking.customer_phone,
    address_street: booking.address_street,
    address_city: booking.address_city,
    address_postal: booking.address_postal,
    notes: booking.notes || "",
    has_outdoor_cold_tap: booking.has_outdoor_cold_tap,
    has_outdoor_hot_tap: booking.has_outdoor_hot_tap,
  });
  const [items, setItems] = useState(
    booking.items.map((it) => ({
      service_id: it.service_id,
      slug: it.slug,
      quantity: it.quantity,
      unit_price: it.unit_price,
      name_en: it.name_en,
      name_pl: it.name_pl,
    })),
  );
  const [saving, setSaving] = useState(false);
  const [showResched, setShowResched] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  const total = useMemo(
    () => items.reduce((s, it) => s + it.unit_price * it.quantity, 0),
    [items],
  );

  const updateItem = (idx, qty) => {
    const next = [...items];
    next[idx] = { ...next[idx], quantity: Math.max(1, qty) };
    setItems(next);
  };
  const removeItem = (idx) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };
  const addService = (svcId) => {
    if (items.find((i) => i.service_id === svcId)) return;
    const svc = services.find((s) => s.id === svcId);
    if (!svc) return;
    setItems([
      ...items,
      {
        service_id: svc.id,
        slug: svc.slug,
        quantity: 1,
        unit_price: svc.base_price,
        name_en: svc.name_en,
        name_pl: svc.name_pl,
      },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({ service_id: it.service_id, quantity: it.quantity })),
      };
      const { data } = await portalApi.put("/portal/booking", payload);
      setBooking(data);
      toast.success(t("portal.saved_toast"));
    } catch (e) {
      toast.error(formatPortalError(e, t, "save"));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    try {
      const { data } = await portalApi.post("/portal/booking/cancel");
      setBooking(data);
      setEditable(false);
      setShowCancel(false);
      toast.success(t("portal.cancelled_toast"));
    } catch (e) {
      toast.error(formatPortalError(e, t, "cancel"));
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-slate-500">{t("portal.scheduled")}</div>
          <div className="font-semibold">
            {booking.scheduled_date} · {booking.scheduled_time}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && (
            <button
              onClick={() => setShowResched(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 font-semibold"
              data-testid="portal-reschedule-btn"
            >
              <CalIcon className="w-4 h-4" /> {t("portal.reschedule")}
            </button>
          )}
          {editable && (
            <button
              onClick={() => setShowCancel(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 font-semibold"
              data-testid="portal-cancel-btn"
            >
              <XCircle className="w-4 h-4" /> {t("portal.cancel")}
            </button>
          )}
        </div>
      </div>

      {/* Services & quantities */}
      <Section title={t("portal.service_label")}>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div
              key={it.service_id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 bg-white"
              data-testid={`portal-item-${it.slug}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {lang === "pl" ? it.name_pl : it.name_en}
                </div>
                <div className="text-xs text-slate-500">
                  {it.unit_price} {t("common.currency")} ×
                </div>
              </div>
              <Input
                type="number"
                min={1}
                value={it.quantity}
                onChange={(e) => updateItem(idx, Number(e.target.value || 1))}
                disabled={!editable}
                className="w-20 text-center"
                data-testid={`portal-item-qty-${it.slug}`}
              />
              <div className="w-20 text-right font-semibold text-sm">
                {(it.unit_price * it.quantity).toFixed(0)} {t("common.currency")}
              </div>
              {editable && items.length > 1 && (
                <button
                  onClick={() => removeItem(idx)}
                  className="text-slate-400 hover:text-rose-600 px-2"
                  aria-label="Remove"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {editable &&
            services
              .filter((s) => !items.find((i) => i.service_id === s.id))
              .map((s) => (
                <button
                  key={s.id}
                  onClick={() => addService(s.id)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-dashed border-sky-300 text-sky-700 hover:bg-sky-50 mr-2"
                  data-testid={`portal-add-service-${s.slug}`}
                >
                  + {lang === "pl" ? s.name_pl : s.name_en}
                </button>
              ))}
        </div>
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-200">
          <span className="text-sm text-slate-500">{t("booking.total")}</span>
          <span className="font-bold text-lg" data-testid="portal-total">
            {total.toFixed(0)} {t("common.currency")}
          </span>
        </div>
      </Section>

      <Section title={t("portal.contact_section")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t("booking.name")}>
            <Input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              disabled={!editable}
              data-testid="portal-name-input"
            />
          </Field>
          <Field label={t("booking.phone")}>
            <Input
              value={form.customer_phone}
              onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
              disabled={!editable}
              data-testid="portal-phone-input"
            />
          </Field>
          <Field label={t("booking.email")} className="sm:col-span-2">
            <Input
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              disabled={!editable}
              data-testid="portal-email-input"
            />
          </Field>
        </div>
      </Section>

      <Section title={t("portal.address_section")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t("booking.street")}>
            <Input
              value={form.address_street}
              onChange={(e) => setForm({ ...form, address_street: e.target.value })}
              disabled={!editable}
              data-testid="portal-street-input"
            />
          </Field>
          <Field label={t("booking.city")}>
            <Select
              value={form.address_city}
              onValueChange={(v) => setForm({ ...form, address_city: v })}
              disabled={!editable}
            >
              <SelectTrigger data-testid="portal-city-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRICITY_CITIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("booking.postal")}>
            <Input
              value={form.address_postal}
              onChange={(e) => setForm({ ...form, address_postal: e.target.value })}
              disabled={!editable}
              data-testid="portal-postal-input"
            />
          </Field>
          <Field label={t("booking.notes")} className="sm:col-span-2">
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              disabled={!editable}
              rows={3}
              data-testid="portal-notes-input"
            />
          </Field>
        </div>
      </Section>

      <Section title={t("portal.taps_section")}>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.has_outdoor_cold_tap}
              onCheckedChange={(v) => setForm({ ...form, has_outdoor_cold_tap: !!v })}
              disabled={!editable}
              data-testid="portal-cold-tap"
            />
            {t("booking.has_cold")}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.has_outdoor_hot_tap}
              onCheckedChange={(v) => setForm({ ...form, has_outdoor_hot_tap: !!v })}
              disabled={!editable}
              data-testid="portal-hot-tap"
            />
            {t("booking.has_hot")}
          </label>
        </div>
      </Section>

      {editable && (
        <div className="flex justify-end pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-1.5"
            data-testid="portal-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t("portal.save")}
          </button>
        </div>
      )}

      {/* Reschedule dialog */}
      {showResched && (
        <RescheduleDialog
          onClose={() => setShowResched(false)}
          onReschedule={(updated) => {
            setBooking(updated);
            setShowResched(false);
            toast.success(t("portal.rescheduled_toast"));
          }}
        />
      )}

      {/* Cancel confirm */}
      <AlertDialog open={showCancel} onOpenChange={setShowCancel}>
        <AlertDialogContent data-testid="portal-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("portal.cancel_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("portal.cancel_confirm_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="portal-cancel-keep-btn">
              {t("portal.cancel_back")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={cancel}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="portal-cancel-confirm-btn"
            >
              {t("portal.cancel_yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5">
      <h3 className="font-display font-semibold text-base mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Reschedule dialog (shadcn AlertDialog kept simple via plain modal)
// ----------------------------------------------------------------------------
function RescheduleDialog({ onClose, onReschedule }) {
  const { t } = useI18n();
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!date) return;
    const ds = formatYMD(date);
    setLoadingSlots(true);
    setTime("");
    api
      .get("/availability", { params: { date: ds } })
      .then((r) => {
        setSlots(r.data.slots || []);
        setBlocked(r.data.blocked);
      })
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async () => {
    if (!date || !time) return;
    setSubmitting(true);
    try {
      const { data } = await portalApi.post("/portal/booking/reschedule", {
        scheduled_date: formatYMD(date),
        scheduled_time: time,
      });
      onReschedule(data);
    } catch (e) {
      toast.error(formatPortalError(e, t, "reschedule"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4"
      data-testid="portal-reschedule-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display font-semibold text-lg mb-3">
          {t("portal.reschedule_title")}
        </h3>
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
          className="rounded-md border"
        />
        <div className="mt-3">
          {loadingSlots ? (
            <div className="text-sm text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> …
            </div>
          ) : blocked ? (
            <div className="text-sm text-rose-600">
              {t("booking.blocked")}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {slots.map((s) => (
                <button
                  key={s.time}
                  disabled={!s.available}
                  onClick={() => setTime(s.time)}
                  className={`px-3 py-2 rounded-lg text-sm border ${
                    !s.available
                      ? "border-slate-200 text-slate-300 cursor-not-allowed"
                      : time === s.time
                        ? "border-sky-600 bg-sky-600 text-white"
                        : "border-slate-200 hover:border-sky-400"
                  }`}
                  data-testid={`portal-slot-${s.time}`}
                >
                  {s.time}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            {t("booking.back")}
          </button>
          <button
            disabled={!date || !time || submitting}
            onClick={submit}
            className="btn-primary disabled:opacity-50"
            data-testid="portal-reschedule-save"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t("portal.reschedule_save")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Customer chat (loads messages, opens WS, posts via REST)
// ----------------------------------------------------------------------------
function CustomerChat({ booking }) {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [conv, setConv] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const peerTypingTimerRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await portalApi.get("/portal/messages");
    setConv(data.conversation);
    setMessages(data.messages);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // WebSocket
  useEffect(() => {
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      const token = getPortalToken();
      if (!token) return;
      const ws = new WebSocket(buildPortalWsUrl(token));
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        if (!cancelled) {
          reconnectRef.current = setTimeout(connect, 2500);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch (_) { /* ignore */ }
      };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "new_message") {
            const m = evt.message;
            setMessages((prev) => {
              if (prev.find((x) => x.id === m.id)) return prev;
              return [...prev, m];
            });
            if (m.sender === "admin") {
              playChime();
              // Mark admin messages as read on the server (also broadcasts read receipt)
              setTimeout(() => portalApi.get("/portal/messages").catch(() => {}), 250);
            }
          } else if (evt.type === "typing" && evt.sender !== "customer") {
            setPeerTyping(true);
            if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
            peerTypingTimerRef.current = setTimeout(() => setPeerTyping(false), 3000);
          } else if (evt.type === "messages_read" && evt.reader === "admin") {
            setMessages((prev) =>
              prev.map((m) =>
                m.sender === "customer" && !m.read ? { ...m, read: true } : m,
              ),
            );
          }
        } catch (_) { /* ignore */ }
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* ignore */ }
      }
    };
  }, []);

  const send = async (body) => {
    const { data } = await portalApi.post("/portal/messages", { body });
    setMessages((prev) => {
      if (prev.find((x) => x.id === data.id)) return prev;
      return [...prev, data];
    });
  };

  const sendTyping = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: "typing" })); } catch (_) { /* ignore */ }
    }
  };

  return (
    <div
      className="flex flex-col"
      style={{ height: "min(70vh, 620px)" }}
      data-testid="portal-chat-wrap"
    >
      <ChatPanel
        fill
        messages={messages}
        onSend={send}
        onTyping={sendTyping}
        currentSender="customer"
        peerTyping={peerTyping}
        wsConnected={wsConnected}
        peerName={t("portal.chat_admin_label")}
        emptyKey="portal.empty_chat"
        placeholderKey="portal.chat_placeholder"
        sendKey="portal.chat_send"
        testidPrefix="portal-chat"
        introKey="portal.chat_intro"
      />
    </div>
  );
}


// ----------------------------------------------------------------------------
// Portal boot skeleton — shown only on a true cold reload of /portal with a
// valid token but no sessionStorage cache (rare).
// ----------------------------------------------------------------------------
function PortalBootSkeleton() {
  return (
    <div
      className="min-h-screen bg-water-grad py-8 sm:py-12 px-4 sm:px-6"
      data-testid="portal-boot-skeleton"
      aria-hidden="true"
    >
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="space-y-3">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-44 w-full rounded-2xl" />
        <Skeleton className="h-44 w-full rounded-2xl" />
        <Skeleton className="h-44 w-full rounded-2xl" />
      </div>
    </div>
  );
}
